<script setup lang="ts">
import { Ref, computed, onMounted, ref, watch, inject } from 'vue';
import { useSenderStore } from '@/stores';
import { colors } from 'quasar';
import { DefaultImageIcon } from '.';
import Tr from '@/i18n/translation';
import { POSITION, useToast } from 'vue-toastification';
import { FileInfo } from '@/stores/sender/sender.state';
import DefaultVideoIcon from './DefaultVideoIcon.vue';

interface DialogObject {
  close(_arg0: { isSended: boolean }): unknown;
  data: {
    mainColor: string;
    buttonColor: string;
    backgroundColor: string;
  };
}

const defaultColors = {
  mainColor: '#000000',
  buttonColor: '#000000',
  backgroundColor: '#ffffff',
};

const dialogRef =
  inject<Ref<DialogObject>>('dialogRef') ??
  ref({ data: defaultColors, close(_arg0) {} });

const { changeAlpha } = colors;
const mainColor = ref(dialogRef.value.data.mainColor);
const buttonColor = ref(dialogRef.value.data.buttonColor);
const backgroundColor = ref(dialogRef.value.data.backgroundColor);

const dialogColors = {
  '--main-color': mainColor.value,
  '--background-color': backgroundColor.value,
  '--border-color': mainColor.value,
  '--p-checkbox-background': backgroundColor.value,
  '--p-checkbox-border-color': mainColor.value,
  '--p-checkbox-checked-background': mainColor.value,
  '--p-checkbox-checked-border-color': mainColor.value,
  '--p-checkbox-checked-hover-background': mainColor.value,
  '--p-checkbox-checked-hover-border-color': backgroundColor.value,
  '--p-checkbox-hover-border-color': mainColor.value,
  '--p-checkbox-icon-checked-color': backgroundColor.value,
  '--p-checkbox-icon-checked-hover-color': backgroundColor.value,
};

const senderStore = useSenderStore();
const toast = useToast();

interface FileUrl {
  isSupported: boolean;
  name: string;
  url: string;
}

const filesUrl = ref<Array<FileUrl>>([]);
const fileRef = ref<HTMLInputElement | null>(null);
const isHidden = ref(false);

const sending = computed(() => {
  return senderStore.isSending;
});

const sendedMB = computed(() => {
  const val = senderStore.sendedSize / 1048576;
  return Math.round((val + Number.EPSILON) * 100) / 100;
});

const totalMB = computed(() => {
  const val = senderStore.totalSizeToSend / 1048576;
  return Math.round((val + Number.EPSILON) * 100) / 100;
});

const btnText = computed(() => {
  if (sending.value) {
    return Tr.t('appCore.apps.event.wedding.sendingText');
  }

  return Tr.t('appCore.apps.event.wedding.sendText');
});

const isBtnDisabled = computed(() => {
  return filesUrl.value.length < 1;
});

const btnColor = computed(() => {
  return buttonColor.value ? buttonColor.value : mainColor.value;
});

const btnColorRunning = computed(() => {
  const color = btnColor.value;
  return changeAlpha(color, -1);
});

const backgroundColorRunning = computed(() => {
  const color = backgroundColor.value;
  return changeAlpha(color, 0.5);
});

function handleEndSending() {
  const files = senderStore.files as Array<FileInfo>;
  const isSendedFile = !files.some((file) => {
    return filesUrl.value[0]?.name === file.file.name;
  });
  if (files.length === 0 || isSendedFile) {
    dialogRef.value.close({
      isSended: true,
    });
  } else {
    const message =
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.sendingError') +
      ' ' +
      Tr.t('appCore.apps.event.sender.infos.sendingProblem.checkFiles');

    toast.error(message, {
      position: POSITION.BOTTOM_CENTER,
    });
  }

  senderStore.sendedInfoShowed = true;
}

watch(
  () => senderStore.filesToSend,
  () => {
    if (senderStore.filesToSend === 0) {
      handleEndSending();
    }
  },
);

const choosePurpose = (file) => {
  let purpose = supportedVideo(file) ? 'video-gallery' : 'photo-gallery';
  if (isHidden.value) {
    purpose = 'hidden-gallery';
  }

  return purpose;
};

watch(isHidden, () => {
  if (!isBtnDisabled.value) {
    senderStore.updatePurpose(
      filesUrl.value[0]?.name ?? '',
      choosePurpose(filesUrl.value[0]),
    );
  }
});

async function sendPicture() {
  if (filesUrl.value.length > 0) {
    const res = await senderStore.sendImages(
      'createSenderStream',
      'sendByStream',
    );

    if (!res) {
      toast.error(Tr.t('appCore.apps.event.sender.errors.default'));
    }
  } else {
    toast.error(Tr.t('appCore.apps.event.sender.errors.notFiles'));
  }

  for (const item of filesUrl.value) {
    URL.revokeObjectURL(item.url);
  }
  senderStore.sendedInfoShowed = false;
}

function onRejected() {
  toast.error(Tr.t('appCore.apps.event.sender.errors.wrongFilesWithWish'));
}

function deleteImg(name) {
  if (sending.value) {
    return;
  }

  senderStore.removeImage(name);
  filesUrl.value = filesUrl.value.filter((item) => {
    if (item.name === name) {
      URL.revokeObjectURL(item.url);
    }
    return item.name !== name;
  });
}

function supportedImage(file) {
  return /^image\//.test(file.type) && !/.heic$/.test(file.name);
}

function supportedVideo(file) {
  return /^video\//.test(file.type);
}

function supportedFile(file) {
  return (
    /.heic$/.test(file.name) || supportedImage(file) || supportedVideo(file)
  );
}

async function previewFile() {
  if (fileRef.value === null || fileRef.value.files === null) {
    return;
  }

  for (const file of fileRef.value.files) {
    const maxSize = 1073741824;
    if (
      supportedFile(file) &&
      filesUrl.value.length < 1 &&
      senderStore.size + file.size < maxSize
    ) {
      senderStore.addImage(file, choosePurpose(file));
      if (supportedImage(file)) {
        filesUrl.value.push({
          isSupported: true,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      } else {
        filesUrl.value.push({
          isSupported: false,
          name: file.name,
          url: URL.createObjectURL(file),
        });
      }
    } else {
      onRejected();
    }
  }
}

onMounted(async () => {
  if (!senderStore.isSending) {
    if (!senderStore.sendedInfoShowed) {
      handleEndSending();
    } else if (senderStore.files.length !== 0) {
      const file = senderStore.files[0] as unknown as FileInfo;
      if (supportedImage(file.file)) {
        filesUrl.value.push({
          isSupported: true,
          name: file.file.name,
          url: URL.createObjectURL(file.file),
        });
      } else {
        filesUrl.value.push({
          isSupported: false,
          name: file.file.name,
          url: URL.createObjectURL(file.file),
        });
      }
    }
  }
});
</script>

<template>
  <div class="content">
    <div class="sender">
      <div class="files">
        <div v-if="filesUrl.length !== 0" class="picture">
          <div class="img-preview img-wish">
            <img
              v-if="filesUrl[0]?.isSupported"
              :src="filesUrl[0]?.url"
              alt=""
            />
            <DefaultImageIcon
              v-else-if="/.heic$/.test(filesUrl[0]?.name ?? '')"
              :color="mainColor"
            />
            <DefaultVideoIcon v-else :color="mainColor" />
            <div class="delete" @click="deleteImg(filesUrl[0]?.name)">
              <span class="plus">+</span>
            </div>
          </div>
        </div>
        <div
          v-if="filesUrl.length === 0"
          class="file-input-wrapper file-input-wrapper-empty"
        >
          <input
            id="file-input-wish"
            ref="fileRef"
            type="file"
            name="file-input"
            class="file-input"
            accept=".jpg, image/*, video/*, .heic"
            :disabled="sending"
            @change="previewFile"
          />
          <label
            for="file-input-wish"
            class="file-input-label"
            :class="{ disabled: sending }"
          >
            <span class="plus">+</span>
            <span class="text">{{
              $t('appCore.apps.components.sender.sendLabel')
            }}</span>
          </label>
        </div>
      </div>
    </div>
    <div class="send">
      <button
        class="button btn-bold"
        :class="{ 'btn-active': sending, 'btn-disabled': isBtnDisabled }"
        :disabled="sending || isBtnDisabled"
        @click="sendPicture"
      >
        <b>{{ btnText }}</b>
        <b v-if="sending && sendedMB !== totalMB"
          >{{ sendedMB }} MB
          {{ $t('appCore.apps.components.sender.from') }}
          {{ totalMB }} MB</b
        >
        <b v-else-if="sending">{{
          $t('appCore.apps.components.sender.processing')
        }}</b>
      </button>
      <div class="input-checkbox">
        <Checkbox
          v-model="isHidden"
          class="input-checkbox-hide-file"
          input-id="isHidden"
          :binary="true"
          :style="dialogColors"
        />
        <label class="input-checkbox-label" for="isHidden">
          {{ $t('appCore.apps.components.sender.notToGallery') }}
        </label>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styleConstans';

.content {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: v-bind(backgroundColor);
}

.sender {
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 80%;
  height: auto;
  margin: 0 auto;
  align-items: stretch;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 2px;
  padding-top: 20px;
  padding-bottom: 170px;
  color: v-bind(mainColor);

  @media screen and (max-width: $media-phone) {
    padding-bottom: 150px;
  }
}

.send {
  position: fixed;
  width: 80%;
  background-color: v-bind(backgroundColor);
  z-index: 10;
  bottom: 0;
  display: flex;
  flex-flow: column wrap;
  justify-content: center;
  align-items: center;
  gap: 15px;
  padding: 5px 0 25px;
}

.button {
  border-radius: 48.707px;
  border: 2px solid v-bind(btnColor);
  color: v-bind(backgroundColor);
  background-color: v-bind(btnColor);
  padding: 15px 25px;
  font-size: 25px;
  white-space: nowrap;
  text-align: center;
  transition: background-color 1s ease;

  &:hover {
    background-color: v-bind(backgroundColor);
    color: v-bind(btnColor);
    cursor: pointer;
  }

  @media screen and (max-width: $media-phone) {
    font-size: 18px;
    line-height: 30px;
    padding: 10px 20px;
  }
}

.btn-disabled {
  opacity: 0.5;
}

.btn-active {
  background-color: v-bind(btnColor);
  color: v-bind(backgroundColor);
  position: relative;
  overflow: hidden;
}

.btn-active::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    to right,
    v-bind(btnColorRunning),
    v-bind(backgroundColorRunning)
  );
  border-radius: 0 26px 26px 0;
  animation: fill-loader 2s linear infinite;
}

@keyframes fill-loader {
  0% {
    left: -100%;
  }

  100% {
    left: 100%;
  }
}

.btn-bold {
  border: 3px solid v-bind(btnColor);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.picture {
  display: flex;
  flex-flow: row wrap;
}

.img-preview {
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (max-width: $media-phone) {
    height: 125px;
    width: 100%;
  }

  img {
    object-fit: cover;
  }

  video {
    width: 150px;
    height: 150px;

    @media screen and (max-width: $media-phone) {
      height: 125px;
      width: 100%;
    }
  }
}

.img-wish {
  width: 100%;
  height: 100%;
}

.files {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

.file-input-wrapper {
  position: relative;
  display: inline-block;
  width: 150px;
  height: 150px;

  @media screen and (max-width: $media-phone) {
    width: 125px;
    height: 125px;
  }
}

.file-input-wrapper-empty {
  width: 100%;
  height: auto;
}

.file-input {
  display: none;
}

.file-input-label {
  cursor: pointer;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: row;
  gap: 15px;
  justify-content: center;
  align-items: center;
  border-radius: 5.476px;
  text-align: center;
  border: 1px solid v-bind(btnColor);
  padding: 15px 40px;

  .text {
    font-size: 1.5em;

    @media screen and (max-width: $media-phone) {
      font-weight: 700;
      font-size: 1em;
    }
  }
}

.plus {
  text-align: center;
  font-size: 3em;
  line-height: 20px;
  border-radius: 50%;
  padding: 5px;
  padding-left: 7px;
  border: 2px solid v-bind(btnColor);

  @media screen and (max-width: $media-phone) {
    font-size: 2.5em;
    line-height: 18px;
  }
}

.delete {
  position: absolute;
  top: -10px;
  right: -10px;

  .plus {
    cursor: pointer;
    background-color: v-bind(backgroundColor);
    display: flex;
    transform: rotate(45deg);
    font-size: 2em;
    line-height: 15px;
    padding: 5px;
    padding-left: 7px;

    @media screen and (max-width: $media-phone) {
      font-size: 2.5em;
      line-height: 17px;
      padding-left: 6px;
    }
  }
}

.input-checkbox {
  display: flex;
  gap: 10px;

  &-hide-file {
    color: v-bind(mainColor);
  }

  &-label {
    color: v-bind(mainColor);
  }
}
</style>
